import { useState, useEffect, type FC, type FormEvent } from 'react';
import { X, Building2, Globe } from 'lucide-react';
import type { Chain, Zone } from '../../../types/api';

interface ChainModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { name: string; zoneId: string }) => Promise<void>;
  chain?: Chain | null;
  zones: Zone[];
  isLoading: boolean;
}

export const ChainModal: FC<ChainModalProps> = ({
  isOpen,
  onClose,
  onSave,
  chain,
  zones,
  isLoading,
}) => {
  const [name, setName] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [errors, setErrors] = useState<{ name?: string; zone?: string }>({});

  useEffect(() => {
    if (isOpen) {
      if (chain) {
        setName(chain.name);
        setZoneId(chain.zoneId);
      } else {
        setName('');
        setZoneId('');
      }
      setErrors({});
    }
  }, [chain, isOpen]);

  const validate = (): boolean => {
    const newErrors: { name?: string; zone?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'El nombre es requerido';
    } else if (name.trim().length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres';
    }

    if (!zoneId) {
      newErrors.zone = 'La zona es requerida';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      await onSave({ name: name.trim(), zoneId });
    } catch (error) {
      console.error('Error al guardar cadena:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-none sm:rounded-lg w-full h-full sm:h-auto sm:max-w-md max-h-screen sm:max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            {chain ? 'Editar Cadena' : 'Nueva Cadena'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 className="inline h-4 w-4 mr-1" />
              Nombre de la Cadena *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({ ...errors, name: undefined });
              }}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ej: OXXO"
              disabled={isLoading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Zona */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Globe className="inline h-4 w-4 mr-1" />
              Zona *
            </label>
            <select
              value={zoneId}
              onChange={(e) => {
                setZoneId(e.target.value);
                if (errors.zone) setErrors({ ...errors, zone: undefined });
              }}
              className={`w-full px-3 py-2.5 sm:py-2 sm:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium ${
                errors.zone ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={isLoading}
            >
              <option value="">Seleccionar zona...</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
            {errors.zone && (
              <p className="mt-1 text-sm text-red-600">{errors.zone}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-medium text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Guardando...' : chain ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
